import type { OrderDocumentKind, OrderRecord } from '@forq/orders-data';
import type { DocumentBlockPlacement, DocumentDefaults, TenantCompanyProfile } from '@forq/platform-data';

/**
 * Vorgangs-Belege als druckfertiges HTML im FTW-Layout, gerendert aus dem Snapshot des Vorgangs:
 *   - Angebot: kaufmännisch, Positionen MIT Preisen + Summen + Konditionen, freibleibend/Gültigkeit.
 *   - Auftragsbestätigung: kaufmännisch, Positionen MIT Preisen + Summen + Konditionen.
 *   - Lieferschein: Positionen OHNE Preise, Lieferanschrift, Empfangsbestätigung.
 *   - Werkstattauftrag: interne Arbeitsanweisung, Maschine + Positionen + interne Texte, keine Preise.
 * Gleicher Briefkopf/Fußzeile wie die übrigen Belege (aus dem Firmenprofil). Gedruckt/als PDF über
 * den Browser (Button im erzeugten HTML).
 */

export const orderDocumentLabels: Readonly<Record<OrderDocumentKind, string>> = {
  offer: 'Angebot',
  confirmation: 'Auftragsbestätigung',
  'delivery-note': 'Lieferschein',
  'workshop-order': 'Werkstattauftrag',
};

// --- Block-Layout (Block-Layout-Editor) -------------------------------------
// Ein Beleg ist eine Folge benannter Blöcke zwischen Belegkopf und Fußzeile. Welche Blöcke eine
// Belegart hat, steht im Katalog (Quelle der Wahrheit); der Mandant darf ihre Reihenfolge/Sichtbarkeit
// je Belegart im Firmenprofil (documentDefaults.blockLayouts) überschreiben. Gesperrte Blöcke bleiben
// immer sichtbar, damit ein Beleg nie ohne seinen Pflichtinhalt (Positionen) ausgeliefert wird.

export type OrderDocumentBlockId =
  | 'parties' | 'customerBox' | 'machine' | 'intro' | 'positions'
  | 'totals' | 'conditions' | 'validity' | 'notes' | 'footerText' | 'signatures';

interface OrderBlockDef {
  readonly id: OrderDocumentBlockId;
  readonly label: string;
  /** true = kann nicht ausgeblendet werden (Pflichtinhalt), nur umsortiert. */
  readonly locked?: boolean;
}

/** Standard-Reihenfolge + Beschriftung + Sperre je Belegart. Der Editor liest hieraus die Blockliste. */
export const orderBlockCatalog: Readonly<Record<OrderDocumentKind, readonly OrderBlockDef[]>> = {
  offer: [
    { id: 'parties', label: 'Anschriften' },
    { id: 'intro', label: 'Anrede / Einleitung' },
    { id: 'positions', label: 'Positionen', locked: true },
    { id: 'totals', label: 'Summen' },
    { id: 'conditions', label: 'Konditionen' },
    { id: 'validity', label: 'Gültigkeit & Preishinweis' },
    { id: 'footerText', label: 'Schlusstext' },
  ],
  confirmation: [
    { id: 'parties', label: 'Anschriften' },
    { id: 'intro', label: 'Anrede / Einleitung' },
    { id: 'positions', label: 'Positionen', locked: true },
    { id: 'totals', label: 'Summen' },
    { id: 'conditions', label: 'Konditionen' },
  ],
  'delivery-note': [
    { id: 'parties', label: 'Anschriften' },
    { id: 'machine', label: 'Maschine' },
    { id: 'intro', label: 'Anrede / Einleitung' },
    { id: 'positions', label: 'Positionen', locked: true },
    { id: 'footerText', label: 'Schlusstext' },
    { id: 'signatures', label: 'Unterschriften' },
  ],
  'workshop-order': [
    { id: 'customerBox', label: 'Auftraggeber' },
    { id: 'machine', label: 'Maschine' },
    { id: 'intro', label: 'Anrede / Einleitung' },
    { id: 'positions', label: 'Arbeiten / Positionen', locked: true },
    { id: 'notes', label: 'Hinweise' },
    { id: 'signatures', label: 'Unterschriften' },
  ],
};

export interface OrderBlockState {
  readonly id: OrderDocumentBlockId;
  readonly label: string;
  readonly locked: boolean;
  readonly hidden: boolean;
}

/**
 * Gespeichertes Layout mit dem Katalog abgleichen: gespeicherte Reihenfolge/Sichtbarkeit gewinnt,
 * unbekannte Ids fliegen raus, neu hinzugekommene Blöcke werden hinten sichtbar angehängt, gesperrte
 * Blöcke sind immer sichtbar. So bleibt ein gespeichertes Layout auch über Schema-Änderungen robust.
 */
export function resolveOrderBlocks(
  kind: OrderDocumentKind,
  blockLayouts: Record<string, ReadonlyArray<DocumentBlockPlacement>> | undefined,
): OrderBlockState[] {
  const catalog = orderBlockCatalog[kind];
  const byId = new Map(catalog.map((def) => [def.id, def]));
  const stored = blockLayouts?.[kind] ?? [];
  const result: OrderBlockState[] = [];
  const seen = new Set<OrderDocumentBlockId>();
  for (const placement of stored) {
    const def = byId.get(placement.id as OrderDocumentBlockId);
    if (!def || seen.has(def.id)) continue;
    seen.add(def.id);
    result.push({ id: def.id, label: def.label, locked: !!def.locked, hidden: def.locked ? false : !!placement.hidden });
  }
  for (const def of catalog) {
    if (seen.has(def.id)) continue;
    result.push({ id: def.id, label: def.label, locked: !!def.locked, hidden: false });
  }
  return result;
}

/** Eine Techniksdaten-Zeile der Maschine (Bezeichnung + fertig formatierter Wert inkl. Einheit). */
export interface OrderDocumentMachineSpec {
  readonly label: string;
  readonly value: string;
}

/**
 * Aufgelöste Maschinen-Technikdaten für den Beleg (vom Aufrufer aus dem Maschinenstamm gebaut, damit
 * documents.ts vom machines-Paket entkoppelt bleibt). Speist die {{maschine.*}}-Platzhalter im Report-
 * Designer: Hersteller/Typ/Seriennummer/Baujahr/Foto + die Technikzeilen (Tragkraft, Hubhöhe, …).
 */
export interface OrderDocumentMachine {
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  /** Baujahr als Text (leer, wenn unbekannt). */
  readonly constructionYear: string;
  /** data:-URI oder URL des Maschinenfotos (leer = keins). */
  readonly imageUrl: string;
  readonly specs: readonly OrderDocumentMachineSpec[];
}

export interface OrderDocumentContext {
  readonly kind: OrderDocumentKind;
  readonly documentNumber: string;
  readonly order: OrderRecord;
  readonly company: TenantCompanyProfile;
  /** Ausstellungsdatum (ISO) -- bei gespeicherten Belegen das createdAt des Belegs. */
  readonly issuedIso: string;
  /** Optionale Maschinen-Technikdaten (für {{maschine.*}} im Report-Designer). */
  readonly machine?: OrderDocumentMachine;
  /**
   * Maschinen JE POSITION, nach machineId. Ein Angebot kann mehrere Maschinen enthalten -- jede
   * Position mit machineId bekommt so ihren eigenen Technikblock samt Foto ({{maschine.*}} INNERHALB
   * der Positionsschleife bzw. maschine_*-Spalten je Zeile im JasperReports-Dienst). Fehlt eine Id
   * hier, greift ersatzweise `machine` (der Einzel-Maschinen-Fall von früher).
   */
  readonly machines?: Readonly<Record<string, OrderDocumentMachine>>;
}

/** Attribut-Schlüssel im Maschinenstamm -> Belegbeschriftung + Einheit (wie im FTW-Angebot). */
const machineSpecLabels: ReadonlyArray<readonly [string, string, string]> = [
  ['capacity', 'Tragfähigkeit', 'kg'],
  ['liftHeight', 'Hubhöhe', 'mm'],
  ['overallHeight', 'Bauhöhe', 'mm'],
  ['drive', 'Antrieb', ''],
  ['hours', 'Betriebsstunden', 'h'],
];

/**
 * Aus einem Maschinenstamm-Datensatz die Beleg-Technikdaten bauen (Aufrufer-Helfer, damit documents.ts
 * vom machines-Paket entkoppelt bleibt -- nimmt nur eine strukturell passende Form). Bekannte Attribute
 * werden mit Beschriftung + Einheit in Zeilen übersetzt.
 */
export function orderMachineFromRecord(machine: {
  readonly manufacturer?: string;
  readonly model?: string;
  readonly serialNumber?: string;
  readonly constructionYear?: number | null;
  readonly imageUrl?: string;
  readonly attributes?: Readonly<Record<string, unknown>>;
}): OrderDocumentMachine {
  const attributes = machine.attributes ?? {};
  const specs = machineSpecLabels
    .filter(([key]) => attributes[key] != null && String(attributes[key]).trim() !== '')
    .map(([key, label, unit]) => ({ label, value: `${String(attributes[key])}${unit ? ` ${unit}` : ''}` }));
  return {
    manufacturer: machine.manufacturer ?? '',
    model: machine.model ?? '',
    serialNumber: machine.serialNumber ?? '',
    constructionYear: machine.constructionYear ? String(machine.constructionYear) : '',
    imageUrl: machine.imageUrl ?? '',
    specs,
  };
}

/** Beispiel-Maschine für die Report-Designer-Vorschau (passt zur previewSampleOrder-Maschine). */
export const sampleTemplateMachine: OrderDocumentMachine = {
  manufacturer: 'Linde',
  model: 'H25',
  serialNumber: 'H2X393U04821',
  constructionYear: '2021',
  imageUrl: '',
  specs: [
    { label: 'Tragfähigkeit', value: '2.500 kg' },
    { label: 'Hubhöhe', value: '3.300 mm' },
    { label: 'Bauhöhe', value: '2.150 mm' },
    { label: 'Antrieb', value: 'Diesel' },
    { label: 'Betriebsstunden', value: '4.200 h' },
  ],
};

// --- Formatierung -----------------------------------------------------------

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
function money(value: number): string { return euro.format(value); }
function qty(value: number): string { return qtyFormat.format(value); }
function dateLabel(value: string): string {
  const [year, month, day] = (value || '').slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : (value || '–');
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** Positionstext ist bereits beim Speichern gesäubert (Editor-Allowlist). Hier eine zweite Sicherung
 *  gegen offensichtliche Gefahren, falls je ein ungeprüfter Wert ankommt -- der Beleg läuft im iframe. */
function richText(value: string): string {
  return (value ?? '')
    .replace(/<\s*(script|style)[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

/**
 * Vorlagen-Text mit Platzhaltern füllen (Vorlagen-Editor): erst den frei editierbaren Text säubern,
 * dann die -- ebenfalls gesäuberten -- Vorgangswerte einsetzen, damit der Beleg im iframe sicher bleibt.
 * Ein leerer Text fällt auf die eingebaute Standard-Anrede zurück.
 */
function fillTemplate(template: string, fallback: string, order: OrderRecord): string {
  return esc(template || fallback)
    .replace(/\{titel\}/g, esc(order.title))
    .replace(/\{kunde\}/g, esc(order.customerName))
    .replace(/\{nummer\}/g, esc(order.number));
}

// --- Sprache: feste Beschriftungen + eingebaute Standard-Anreden je Belegsprache -----------------
export type DocLang = 'de' | 'en';

/** Belegsprache aus dem Vorgang ableiten (nur en schaltet um; alles andere = Deutsch als Standard). */
function langOf(order: OrderRecord): DocLang {
  return order.conditions.language === 'en' ? 'en' : 'de';
}

interface DocStrings {
  readonly title: Readonly<Record<OrderDocumentKind, string>>;
  readonly print: string; readonly docNo: string; readonly caseNo: string; readonly date: string; readonly custNo: string;
  readonly contact: string; readonly bankDetails: string; readonly company: string;
  readonly phone: string; readonly fax: string; readonly director: string; readonly vatId: string;
  readonly pos: string; readonly description: string; readonly qty: string; readonly unit: string; readonly unitPrice: string; readonly discount: string; readonly net: string;
  readonly subtotal: string; readonly lineDiscounts: string; readonly orderDiscount: string; readonly netAmount: string; readonly plusVat: string; readonly total: string;
  readonly yourRef: string; readonly paymentTerm: (days: number) => string; readonly cashDiscount: (p: string, d: number) => string; readonly shipping: string; readonly deliveryTerm: string; readonly requestedDate: string;
  readonly machine: string; readonly machineNo: string;
  readonly quotationTo: string; readonly deliveryAddress: string; readonly billingAddress: string; readonly customer: string; readonly technician: string; readonly due: string; readonly workMaterial: string; readonly notes: string;
  readonly validity: (days: number) => string; readonly pricesExclVat: string;
  readonly goodsReceived: string; readonly recipientSignature: string; readonly workPerformed: string; readonly inspection: string;
  readonly offerIntro: string; readonly confirmationIntro: string; readonly deliveryNoteIntro: string; readonly workshopOrderIntro: string;
}

const STRINGS: Readonly<Record<DocLang, DocStrings>> = {
  de: {
    title: { offer: 'Angebot', confirmation: 'Auftragsbestätigung', 'delivery-note': 'Lieferschein', 'workshop-order': 'Werkstattauftrag' },
    print: 'Drucken / als PDF speichern', docNo: 'Beleg-Nr.:', caseNo: 'Vorgang:', date: 'Datum:', custNo: 'Kunden-Nr.:',
    contact: 'Kontakt', bankDetails: 'Bankverbindung', company: 'Unternehmen',
    phone: 'Tel.:', fax: 'Fax:', director: 'Geschäftsführer:', vatId: 'USt-IdNr.:',
    pos: 'Pos.', description: 'Bezeichnung', qty: 'Menge', unit: 'Einheit', unitPrice: 'Einzelpreis', discount: 'Rabatt', net: 'Netto',
    subtotal: 'Zwischensumme', lineDiscounts: 'Positionsrabatte', orderDiscount: 'Auftragsrabatt', netAmount: 'Nettobetrag', plusVat: 'zzgl. Umsatzsteuer', total: 'Gesamtbetrag',
    yourRef: 'Ihre Referenz:', paymentTerm: (days) => `Zahlungsziel: ${days} Tage`, cashDiscount: (p, d) => `${p} % Skonto bei Zahlung innerhalb ${d} Tagen`, shipping: 'Versand:', deliveryTerm: 'Lieferbedingung:', requestedDate: 'Wunschtermin:',
    machine: 'Maschine', machineNo: 'Maschinennummer',
    quotationTo: 'Angebot an', deliveryAddress: 'Lieferanschrift', billingAddress: 'Rechnungsanschrift', customer: 'Auftraggeber', technician: 'Bearbeiter', due: 'Fällig', workMaterial: 'Auszuführende Arbeit / Material', notes: 'Hinweise',
    validity: (days) => days > 0 ? `Dieses Angebot ist freibleibend und gültig für ${days} Tage ab Angebotsdatum. ` : 'Dieses Angebot ist freibleibend. ', pricesExclVat: 'Alle Preise verstehen sich zzgl. der gesetzlichen Umsatzsteuer.',
    goodsReceived: 'Ware vollständig und unbeschädigt erhalten', recipientSignature: 'Ort, Datum, Unterschrift Empfänger', workPerformed: 'Arbeit ausgeführt am / durch', inspection: 'Prüfung / Freigabe',
    offerIntro: 'gerne unterbreiten wir Ihnen zu „{titel}" folgendes Angebot:', confirmationIntro: 'wir bestätigen Ihren Auftrag „{titel}" wie folgt:', deliveryNoteIntro: 'Lieferung zum Vorgang „{titel}":', workshopOrderIntro: 'Arbeitsauftrag „{titel}":',
  },
  en: {
    title: { offer: 'Quotation', confirmation: 'Order Confirmation', 'delivery-note': 'Delivery Note', 'workshop-order': 'Work Order' },
    print: 'Print / save as PDF', docNo: 'Document No.:', caseNo: 'Reference:', date: 'Date:', custNo: 'Customer No.:',
    contact: 'Contact', bankDetails: 'Bank details', company: 'Company',
    phone: 'Phone:', fax: 'Fax:', director: 'Managing Director:', vatId: 'VAT ID:',
    pos: 'Item', description: 'Description', qty: 'Qty', unit: 'Unit', unitPrice: 'Unit price', discount: 'Discount', net: 'Net',
    subtotal: 'Subtotal', lineDiscounts: 'Line discounts', orderDiscount: 'Order discount', netAmount: 'Net amount', plusVat: 'plus VAT', total: 'Total',
    yourRef: 'Your reference:', paymentTerm: (days) => `Payment term: ${days} days`, cashDiscount: (p, d) => `${p} % cash discount if paid within ${d} days`, shipping: 'Shipping:', deliveryTerm: 'Delivery terms:', requestedDate: 'Requested date:',
    machine: 'Machine', machineNo: 'Machine number',
    quotationTo: 'Quotation to', deliveryAddress: 'Delivery address', billingAddress: 'Billing address', customer: 'Customer', technician: 'Technician', due: 'Due', workMaterial: 'Work to be performed / material', notes: 'Notes',
    validity: (days) => days > 0 ? `This quotation is without engagement and valid for ${days} days from the quotation date. ` : 'This quotation is without engagement. ', pricesExclVat: 'All prices are exclusive of statutory VAT.',
    goodsReceived: 'Goods received complete and undamaged', recipientSignature: 'Place, date, recipient\'s signature', workPerformed: 'Work performed on / by', inspection: 'Inspection / approval',
    offerIntro: 'we are pleased to submit the following quotation for „{titel}":', confirmationIntro: 'we hereby confirm your order „{titel}" as follows:', deliveryNoteIntro: 'delivery for case „{titel}":', workshopOrderIntro: 'work order „{titel}":',
  },
};

// --- Briefkopf / Fußzeile (aus dem Firmenprofil) ---------------------------

function letterhead(company: TenantCompanyProfile): string {
  const identity = company.identity;
  const lh = company.letterhead;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `
    <div class="hd">
      <div class="hd-slot hd-left">${slotLeft}</div>
      <div class="hd-slot hd-center">${slotCenter}</div>
      <div class="hd-slot hd-right">${slotRight}</div>
    </div>
    <hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile, lang: DocLang): string {
  const L = STRINGS[lang];
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col(L.contact, [contact.phone && `${L.phone} ${contact.phone}`, contact.fax && `${L.fax} ${contact.fax}`, contact.email, contact.website])}
    ${col(L.bankDetails, [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col(L.company, [identity.managingDirectors && `${L.director} ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `${L.vatId} ${identity.vatId}`])}
  </div>`;
}

// --- Bausteine --------------------------------------------------------------

function addressBlock(order: OrderRecord, kind: 'billing' | 'delivery'): string {
  const address = order.addresses.find((entry) => entry.kind === kind)
    ?? order.addresses.find((entry) => entry.kind === 'billing')
    ?? order.addresses[0];
  const name = address?.companyName || order.customerName;
  const lines = [
    name,
    address?.contactName || order.contactName,
    address?.street,
    `${address?.postalCode ?? ''} ${address?.city ?? ''}`.trim(),
  ].filter(Boolean);
  return lines.map((line) => esc(line as string)).join('<br>');
}

function pricedPositions(order: OrderRecord, lang: DocLang): string {
  const L = STRINGS[lang];
  const rows = order.positions.map((position) => `<tr>
      <td class="c">${position.positionNumber}</td>
      <td>${esc(position.description)}${position.customerText ? `<div class="postext">${richText(position.customerText)}</div>` : ''}</td>
      <td class="num">${position.kind === 'text' ? '' : qty(position.quantity)}</td>
      <td>${esc(position.unit)}</td>
      <td class="num">${position.kind === 'text' ? '' : money(position.unitPrice)}</td>
      <td class="num">${position.discountPercent > 0 ? `${qty(position.discountPercent)} %` : ''}</td>
      <td class="num">${position.kind === 'text' ? '' : money(position.netAmount)}</td>
    </tr>`).join('');
  return `<table class="pos">
    <thead><tr><th class="c">${L.pos}</th><th>${L.description}</th><th class="num">${L.qty}</th><th>${L.unit}</th><th class="num">${L.unitPrice}</th><th class="num">${L.discount}</th><th class="num">${L.net}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

function totalsBlock(order: OrderRecord, lang: DocLang): string {
  const L = STRINGS[lang];
  const row = (label: string, value: string, strong = false) =>
    `<tr class="${strong ? 'is-strong' : ''}"><th>${esc(label)}</th><td>${value}</td></tr>`;
  return `<table class="totals">
    ${order.positionDiscountTotal > 0 ? row(L.subtotal, money(order.subtotal)) : ''}
    ${order.positionDiscountTotal > 0 ? row(L.lineDiscounts, `− ${money(order.positionDiscountTotal)}`) : ''}
    ${order.orderDiscountTotal > 0 ? row(L.orderDiscount, `− ${money(order.orderDiscountTotal)}`) : ''}
    ${row(L.netAmount, money(order.netTotal))}
    ${row(L.plusVat, money(order.vatTotal))}
    ${row(L.total, money(order.grossTotal), true)}
  </table>`;
}

function conditionsBlock(order: OrderRecord, lang: DocLang): string {
  const L = STRINGS[lang];
  const conditions = order.conditions;
  const parts: string[] = [];
  if (conditions.customerReference) parts.push(`${L.yourRef} ${conditions.customerReference}`);
  parts.push(L.paymentTerm(conditions.paymentTermDays));
  if (conditions.cashDiscountPercent > 0) parts.push(L.cashDiscount(qty(conditions.cashDiscountPercent), conditions.cashDiscountDays));
  if (conditions.deliveryMethod) parts.push(`${L.shipping} ${conditions.deliveryMethod}`);
  if (conditions.deliveryTerms) parts.push(`${L.deliveryTerm} ${conditions.deliveryTerms}`);
  if (conditions.requestedDate) parts.push(`${L.requestedDate} ${dateLabel(conditions.requestedDate)}`);
  return `<p class="note">${parts.map(esc).join(' · ')}</p>`;
}

function machineBlock(order: OrderRecord, lang: DocLang): string {
  if (!order.machineNumber && !order.machineLabel) return '';
  const L = STRINGS[lang];
  return `<table class="box">
    <tr><th>${L.machine}</th><td>${esc(order.machineLabel || '–')}</td></tr>
    <tr><th>${L.machineNo}</th><td>${esc(order.machineNumber || '–')}</td></tr>
  </table>`;
}

function deliveryPositions(order: OrderRecord, lang: DocLang): string {
  const L = STRINGS[lang];
  const rows = order.positions
    .filter((position) => position.kind !== 'text' || position.description)
    .map((position) => `<tr>
      <td class="c">${position.positionNumber}</td>
      <td>${esc(position.description)}${position.customerText ? `<div class="postext">${richText(position.customerText)}</div>` : ''}</td>
      <td class="num">${position.kind === 'text' ? '' : qty(position.quantity)}</td>
      <td>${esc(position.unit)}</td>
    </tr>`).join('');
  return `<table class="pos">
      <thead><tr><th class="c">${L.pos}</th><th>${L.description}</th><th class="num">${L.qty}</th><th>${L.unit}</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function workshopPositions(order: OrderRecord, lang: DocLang): string {
  const L = STRINGS[lang];
  const rows = order.positions.map((position) => `<tr>
      <td class="c">${position.positionNumber}</td>
      <td>${esc(position.description)}${position.internalText ? `<br><small>${esc(position.internalText)}</small>` : ''}</td>
      <td class="num">${position.kind === 'text' ? '' : qty(position.quantity)}</td>
      <td>${esc(position.unit)}</td>
    </tr>`).join('');
  return `<table class="pos">
      <thead><tr><th class="c">${L.pos}</th><th>${L.workMaterial}</th><th class="num">${L.qty}</th><th>${L.unit}</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

// --- Belegkörper: benannte Blöcke + Zusammenbau nach Layout ------------------

/** Alle Blöcke einer Belegart als fertiges HTML, je Block-Id. Blöcke, die es für die Art nicht gibt,
 *  fehlen im Map; ein für die Art vorhandener, aber inhaltlich leerer Block ist ''. */
function orderBlockHtml(kind: OrderDocumentKind, order: OrderRecord, defaults: DocumentDefaults, lang: DocLang): Partial<Record<OrderDocumentBlockId, string>> {
  const L = STRINGS[lang];
  const hasDelivery = order.addresses.some((a) => a.kind === 'delivery');
  const blocks: Partial<Record<OrderDocumentBlockId, string>> = {};

  if (kind === 'offer') {
    blocks.parties = `<div class="parties">
      <div><span class="pl">${L.quotationTo}</span>${addressBlock(order, 'billing')}</div>
      ${hasDelivery ? `<div><span class="pl">${L.deliveryAddress}</span>${addressBlock(order, 'delivery')}</div>` : ''}
    </div>`;
  } else if (kind === 'confirmation') {
    blocks.parties = `<div class="parties">
      <div><span class="pl">${L.billingAddress}</span>${addressBlock(order, 'billing')}</div>
      ${hasDelivery ? `<div><span class="pl">${L.deliveryAddress}</span>${addressBlock(order, 'delivery')}</div>` : ''}
    </div>`;
  } else if (kind === 'delivery-note') {
    blocks.parties = `<div class="parties">
      <div><span class="pl">${L.deliveryAddress}</span>${addressBlock(order, 'delivery')}</div>
      <div><span class="pl">${L.customer}</span>${addressBlock(order, 'billing')}</div>
    </div>`;
  }

  if (kind === 'workshop-order') {
    blocks.customerBox = `<table class="box">
      <tr><th>${L.customer}</th><td>${esc(order.customerName || '–')}${order.customerNumber ? ` (${esc(order.customerNumber)})` : ''}</td></tr>
      <tr><th>${L.technician}</th><td>${esc(order.assignedTo || '–')}</td></tr>
      ${order.dueAt ? `<tr><th>${L.due}</th><td>${dateLabel(order.dueAt)}</td></tr>` : ''}
    </table>`;
  }

  if (kind === 'delivery-note' || kind === 'workshop-order') {
    blocks.machine = machineBlock(order, lang);
  }

  const introText = kind === 'offer' ? fillTemplate(lang === 'de' ? defaults.offerIntro : '', L.offerIntro, order)
    : kind === 'confirmation' ? fillTemplate(lang === 'de' ? defaults.confirmationIntro : '', L.confirmationIntro, order)
      : kind === 'delivery-note' ? fillTemplate(lang === 'de' ? defaults.deliveryNoteIntro : '', L.deliveryNoteIntro, order)
        : fillTemplate(lang === 'de' ? defaults.workshopOrderIntro : '', L.workshopOrderIntro, order);
  blocks.intro = `<p class="intro">${introText}</p>`;

  blocks.positions = kind === 'offer' || kind === 'confirmation' ? pricedPositions(order, lang)
    : kind === 'delivery-note' ? deliveryPositions(order, lang)
      : workshopPositions(order, lang);

  if (kind === 'offer' || kind === 'confirmation') {
    blocks.totals = totalsBlock(order, lang);
    blocks.conditions = conditionsBlock(order, lang);
  }
  if (kind === 'offer') {
    blocks.validity = `<p class="note">${L.validity(defaults.quoteValidityDays)}${L.pricesExclVat}</p>`;
    blocks.footerText = lang === 'de' && defaults.quoteFooterText ? `<p class="note">${esc(defaults.quoteFooterText)}</p>` : '';
  }
  if (kind === 'delivery-note') {
    blocks.footerText = lang === 'de' && defaults.deliveryNoteFooterText ? `<p class="note">${esc(defaults.deliveryNoteFooterText)}</p>` : '';
    blocks.signatures = `<div class="sign">
      <div><span></span>${L.goodsReceived}</div>
      <div><span></span>${L.recipientSignature}</div>
    </div>`;
  }
  if (kind === 'workshop-order') {
    blocks.notes = order.notes ? `<h2 class="sec">${L.notes}</h2><p class="note">${esc(order.notes)}</p>` : '';
    blocks.signatures = `<div class="sign">
      <div><span></span>${L.workPerformed}</div>
      <div><span></span>${L.inspection}</div>
    </div>`;
  }

  return blocks;
}

/** Belegkörper aus den Blöcken in der (ggf. mandantenspezifischen) Reihenfolge, ausgeblendete weggelassen.
 *  `exclude` lässt Blöcke weg, die im Grundbriefbogen-Modus schon der Rahmen trägt (z. B. Anschriften). */
function assembleOrderBody(kind: OrderDocumentKind, order: OrderRecord, defaults: DocumentDefaults, lang: DocLang, exclude?: ReadonlySet<OrderDocumentBlockId>): string {
  const blocks = orderBlockHtml(kind, order, defaults, lang);
  return resolveOrderBlocks(kind, defaults.blockLayouts)
    .filter((state) => !state.hidden && !exclude?.has(state.id))
    .map((state) => blocks[state.id] ?? '')
    .join('\n    ');
}

function shell(ctx: OrderDocumentContext): string {
  const { company, order, kind, documentNumber, issuedIso } = ctx;
  const lang = langOf(order);
  const L = STRINGS[lang];
  const accent = company.letterhead.accentColor || '#e30613';
  const label = L.title[kind];
  const body = assembleOrderBody(kind, order, company.documentDefaults, lang);
  return `<!doctype html>
<html lang="${esc(lang)}"><head><meta charset="utf-8"><title>${esc(label)} ${esc(documentNumber)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 9mm; }
  .doc-head { min-width: 74mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 18px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .parties { display: flex; justify-content: space-between; gap: 12mm; margin-top: 8mm; font-size: 11.5px; }
  .parties .pl { display: block; color: #888; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
  .intro { margin: 7mm 0 3mm; }
  h2.sec { font-size: 13px; font-weight: 700; margin: 6mm 0 2mm; }
  table.box { border-collapse: collapse; border: 1px solid #ccc; margin-top: 5mm; min-width: 90mm; }
  table.box th { text-align: left; color: var(--label); font-weight: 700; padding: 4px 8px; border: 1px solid #e2e2e2; background: #fafafa; width: 42mm; }
  table.box td { padding: 4px 8px; border: 1px solid #e2e2e2; }
  table.pos { width: 100%; border-collapse: collapse; margin-top: 3mm; font-size: 11px; }
  table.pos th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 7px; border-bottom: 1.5px solid var(--accent); background: #fafafa; }
  table.pos td { padding: 5px 7px; border-bottom: 1px solid #e6e6e6; vertical-align: top; }
  table.pos td.c, table.pos th.c { text-align: center; width: 10mm; }
  table.pos .num { text-align: right; white-space: nowrap; }
  table.pos small { color: #666; }
  table.pos .postext { margin-top: 3px; font-size: 10.5px; color: #333; line-height: 1.45; }
  table.pos .postext span { line-height: inherit; }
  table.totals { margin: 4mm 0 0 auto; border-collapse: collapse; min-width: 78mm; font-size: 11.5px; }
  table.totals th { text-align: left; color: #444; font-weight: 500; padding: 3px 12px 3px 0; }
  table.totals td { text-align: right; font-weight: 700; padding: 3px 0; white-space: nowrap; }
  table.totals tr.is-strong th, table.totals tr.is-strong td { border-top: 1.5px solid var(--accent); font-size: 13px; color: #111; padding-top: 5px; }
  .note { font-size: 10.5px; color: #444; margin: 4mm 0 0; text-align: justify; white-space: pre-line; }
  .sign { display: flex; gap: 14mm; margin-top: 16mm; }
  .sign div { flex: 1; font-size: 10px; color: #555; }
  .sign span { display: block; border-bottom: 1px solid #333; margin-bottom: 4px; height: 12mm; }
  .footer { margin-top: 12mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    table.pos thead { display: table-header-group; }
    table.pos tr, .sign, table.totals { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">${L.print}</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band">
      <div class="sender">${esc(company.identity.name || '')}</div>
      <div class="doc-head">
        <div class="doc-title">${esc(label)}</div>
        <table class="meta">
          <tr><th>${L.docNo}</th><td>${esc(documentNumber)}</td></tr>
          <tr><th>${L.caseNo}</th><td>${esc(order.number)}</td></tr>
          <tr><th>${L.date}</th><td>${dateLabel(issuedIso)}</td></tr>
          ${order.customerNumber ? `<tr><th>${L.custNo}</th><td>${esc(order.customerNumber)}</td></tr>` : ''}
        </table>
      </div>
    </div>
    ${body}
    ${footer(company, lang)}
  </div>
</body></html>`;
}

// --- Report-Designer: Datenmodell + Vorlagen-Renderer -----------------------
// Eine frei gestaltete Vorlage (GrapesJS) ist HTML mit Platzhaltern. Damit sie ohne Programmierung
// befüllbar ist, stellen wir die Vorgangsdaten als flaches, deutsch benanntes Objekt bereit
// (Zahlen/Datum schon formatiert), das der Gestalter über {{pfad.zum.feld}} anzapft.

function addressData(order: OrderRecord, kind: 'billing' | 'delivery'): Record<string, string> | null {
  const address = order.addresses.find((entry) => entry.kind === kind)
    ?? (kind === 'billing' ? order.addresses[0] : undefined);
  if (!address) return null;
  return {
    name: address.companyName || order.customerName,
    ansprechpartner: address.contactName || order.contactName || '',
    strasse: address.street || '',
    plz: address.postalCode || '',
    ort: address.city || '',
    land: address.countryCode || '',
  };
}

/** Alle Belegdaten als flaches, deutsch benanntes Objekt für die Platzhalter der Report-Vorlage. */
export function buildOrderTemplateData(ctx: OrderDocumentContext): Record<string, unknown> {
  const { order, company, kind, documentNumber, issuedIso } = ctx;
  const lang = langOf(order);
  const L = STRINGS[lang];
  const { identity, contact, bank, letterhead } = company;
  // Die Maschine ZUR POSITION: erst die je-Position-Karte, ersatzweise die Einzelmaschine des Belegs.
  const machineForPosition = (machineId: string | null): OrderDocumentMachine | undefined => {
    if (!machineId) return undefined;
    return ctx.machines?.[machineId] ?? (order.machineId === machineId ? ctx.machine : undefined);
  };
  const positionen = order.positions.map((position) => {
    const positionMachine = machineForPosition(position.machineId);
    return {
      pos: position.positionNumber,
      art: position.kind,
      bezeichnung: position.description,
      kundentext: position.customerText || '',
      menge: position.kind === 'text' ? '' : qty(position.quantity),
      einheit: position.unit || '',
      einzelpreis: position.kind === 'text' ? '' : money(position.unitPrice),
      rabatt: position.discountPercent > 0 ? `${qty(position.discountPercent)} %` : '',
      ustsatz: position.kind === 'text' ? '' : `${qty(position.vatRate)} %`,
      netto: position.kind === 'text' ? '' : money(position.netAmount),
      // Technikblock + Foto der Maschine DIESER Position (leer, wenn die Position keine Maschine ist).
      maschine: positionMachine
        ? {
          hersteller: positionMachine.manufacturer,
          typ: positionMachine.model,
          seriennummer: positionMachine.serialNumber,
          baujahr: positionMachine.constructionYear,
          foto: positionMachine.imageUrl,
          technik: positionMachine.specs.map((spec) => ({ bezeichnung: spec.label, wert: spec.value })),
        }
        : null,
    };
  });
  const billing = addressData(order, 'billing');
  return {
    belegart: L.title[kind],
    nummer: documentNumber,
    vorgangsnummer: order.number,
    datum: dateLabel(issuedIso),
    sprache: lang,
    titel: order.title,
    bearbeiter: order.assignedTo || '',
    faellig: order.dueAt ? dateLabel(order.dueAt) : '',
    hinweise: order.notes || '',
    firma: {
      name: identity.name,
      strasse: identity.address.street,
      plz: identity.address.postalCode,
      ort: identity.address.city,
      telefon: contact.phone,
      fax: contact.fax,
      email: contact.email,
      web: contact.website,
      ustId: identity.vatId,
      steuernr: identity.taxNumber,
      geschaeftsfuehrer: identity.managingDirectors,
      register: identity.registerCourt && identity.register ? `${identity.registerCourt} ${identity.register}` : identity.register,
      bank: bank.bankName,
      iban: bank.iban,
      bic: bank.bic,
      logo: letterhead.logoDataUrl,
      logoMitte: letterhead.logoCenterDataUrl,
      logoRechts: letterhead.logoRightDataUrl,
      akzentfarbe: letterhead.accentColor || '#e30613',
    },
    kunde: {
      nummer: order.customerNumber,
      name: billing?.name ?? order.customerName,
      ansprechpartner: billing?.ansprechpartner ?? order.contactName ?? '',
      strasse: billing?.strasse ?? '',
      plz: billing?.plz ?? '',
      ort: billing?.ort ?? '',
      land: billing?.land ?? '',
    },
    lieferadresse: addressData(order, 'delivery'),
    maschine: order.machineNumber || order.machineLabel || ctx.machine
      ? {
        nummer: order.machineNumber || '',
        bezeichnung: order.machineLabel || '',
        hersteller: ctx.machine?.manufacturer ?? '',
        typ: ctx.machine?.model ?? '',
        seriennummer: ctx.machine?.serialNumber ?? '',
        baujahr: ctx.machine?.constructionYear ?? '',
        foto: ctx.machine?.imageUrl ?? '',
        technik: (ctx.machine?.specs ?? []).map((spec) => ({ bezeichnung: spec.label, wert: spec.value })),
      }
      : null,
    positionen,
    summen: {
      zwischensumme: money(order.subtotal),
      positionsrabatte: money(order.positionDiscountTotal),
      auftragsrabatt: money(order.orderDiscountTotal),
      netto: money(order.netTotal),
      ust: money(order.vatTotal),
      brutto: money(order.grossTotal),
    },
    konditionen: {
      referenz: order.conditions.customerReference || '',
      zahlungsziel: `${order.conditions.paymentTermDays}`,
      skontoProzent: order.conditions.cashDiscountPercent > 0 ? qty(order.conditions.cashDiscountPercent) : '',
      skontoTage: order.conditions.cashDiscountDays > 0 ? `${order.conditions.cashDiscountDays}` : '',
      versand: order.conditions.deliveryMethod || '',
      lieferbedingung: order.conditions.deliveryTerms || '',
      wunschtermin: order.conditions.requestedDate ? dateLabel(order.conditions.requestedDate) : '',
      kostenstelle: order.conditions.costCenterName || '',
    },
  };
}

/**
 * Vorgangsdaten fuer den externen JasperReports-Dienst aufbereiten: das verschachtelte Objekt aus
 * buildOrderTemplateData wird zu FLACHEN Parametern mit Unterstrich-Pfad (firma.name -> firma_name),
 * die Positionen zu `rows` (je Zeile die Spalten pos/bezeichnung/menge/einheit/einzelpreis/rabatt/
 * ustsatz/netto). Die Maschinen-Technikdaten (Liste) werden zu einer mehrzeiligen Zeichenkette
 * `maschine_technik` zusammengefasst (label: wert je Zeile) -- so braucht die Vorlage keinen
 * Unterbericht. Logo-Base64 wird ausgelassen (bloeht die Anfrage nur auf). Alle Werte sind Strings;
 * die Namen entsprechen den $P{...}/$F{...} in der .jrxml und der sample.json des Dienstes.
 */
/** Aus einer Bildquelle den reinen Base64-Teil ziehen (data:-URI -> Nutzlast; alles andere unverändert). */
function base64Payload(source: string): string {
  const marker = 'base64,';
  const at = source.indexOf(marker);
  return at >= 0 ? source.slice(at + marker.length) : source;
}

export function buildOrderReportPayload(ctx: OrderDocumentContext): {
  params: Record<string, string>;
  rows: Array<Record<string, string>>;
} {
  const data = buildOrderTemplateData(ctx);
  const params: Record<string, string> = {};
  const rows = Array.isArray(data.positionen)
    ? (data.positionen as Array<Record<string, unknown>>).map((row) => {
      const out: Record<string, string> = {};
      for (const [key, value] of Object.entries(row)) {
        if (key === 'maschine') {
          // Maschine der Position flach als maschine_* -- so wiederholt das Detail-Band von Jasper
          // den Technikblock automatisch je Maschine, ohne Platzhalter je Position in der Vorlage.
          const machine = value as Record<string, unknown> | null;
          out.maschine_hersteller = machine ? String(machine.hersteller ?? '') : '';
          out.maschine_typ = machine ? String(machine.typ ?? '') : '';
          out.maschine_seriennummer = machine ? String(machine.seriennummer ?? '') : '';
          out.maschine_baujahr = machine ? String(machine.baujahr ?? '') : '';
          out.maschine_technik = machine && Array.isArray(machine.technik)
            ? (machine.technik as Array<Record<string, unknown>>).map((spec) => `${spec.bezeichnung ?? ''}: ${spec.wert ?? ''}`).join('\n')
            : '';
          // Foto als REINES Base64 (ohne "data:image/...;base64,"): JasperReports kann keine data-URIs
          // laden, dekodiert aber Base64 im Bild-Ausdruck zu byte[].
          out.maschine_foto = machine ? base64Payload(String(machine.foto ?? '')) : '';
          continue;
        }
        out[key] = value == null ? '' : String(value);
      }
      return out;
    })
    : [];
  const walk = (obj: Record<string, unknown>, prefix: string): void => {
    for (const [key, value] of Object.entries(obj)) {
      if (prefix === '' && key === 'positionen') continue; // -> rows
      if (prefix === 'firma' && (key === 'logo' || key === 'logoMitte' || key === 'logoRechts')) continue;
      const path = prefix ? `${prefix}_${key}` : key;
      if (value == null) { params[path] = ''; continue; }
      if (Array.isArray(value)) {
        if (key === 'technik') {
          params[path] = (value as Array<Record<string, unknown>>)
            .map((spec) => `${spec.bezeichnung ?? ''}: ${spec.wert ?? ''}`)
            .join('\n');
        }
        continue; // andere Listen in v1 ausgelassen
      }
      if (typeof value === 'object') { walk(value as Record<string, unknown>, path); continue; }
      params[path] = path === 'maschine_foto' ? base64Payload(String(value)) : String(value);
    }
  };
  walk(data, '');
  return { params, rows };
}

function resolveTemplateValue(scopes: ReadonlyArray<Record<string, unknown>>, path: string): unknown {
  const keys = path.split('.');
  for (const scope of scopes) {
    let current: unknown = scope;
    let found = true;
    for (const key of keys) {
      if (current != null && typeof current === 'object' && key in (current as Record<string, unknown>)) {
        current = (current as Record<string, unknown>)[key];
      } else { found = false; break; }
    }
    if (found && current != null) return current;
  }
  return undefined;
}

function fillTemplateScalars(html: string, scopes: ReadonlyArray<Record<string, unknown>>): string {
  return html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, path: string) => {
    const value = resolveTemplateValue(scopes, path);
    return value == null || typeof value === 'object' ? '' : esc(String(value));
  });
}

/**
 * Attribut-basierte Wiederholung (GrapesJS-freundlich, da Attribute den Export überleben): ein Element
 * mit `data-forq-repeat="positionen"` ist die Vorlagenzeile und wird je Listeneintrag wiederholt. Läuft
 * über den DOM (im Browser und in jsdom vorhanden); ohne DOM bleibt das HTML unverändert (Kommentar-
 * Variante greift dann weiter). Das Element wird durch einen Marker ersetzt und danach zeilenweise
 * eingesetzt — so bleibt die Tabellenstruktur beim Serialisieren heil.
 */
function applyAttributeRepeats(html: string, data: Record<string, unknown>): string {
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const repeats = Array.from(doc.querySelectorAll('[data-forq-repeat]'));
  if (repeats.length === 0) return html;
  const chunks: string[] = [];
  repeats.forEach((element, index) => {
    const path = element.getAttribute('data-forq-repeat') || '';
    const list = resolveTemplateValue([data], path);
    element.removeAttribute('data-forq-repeat');
    const rowTemplate = element.outerHTML;
    chunks[index] = Array.isArray(list)
      ? list.map((item) => fillTemplateScalars(rowTemplate, [item as Record<string, unknown>, data])).join('')
      : '';
    element.replaceWith(doc.createComment(`__forqrep_${index}__`));
  });
  let out = doc.body.innerHTML;
  chunks.forEach((chunk, index) => { out = out.replace(`<!--__forqrep_${index}__-->`, chunk); });
  return out;
}

/**
 * Eine Report-Vorlage mit den Vorgangsdaten füllen. Konstrukte:
 *   {{pfad.zum.feld}}                                      Skalar, HTML-escaped eingesetzt
 *   data-forq-repeat="positionen" (Attribut am Element)    Element je Listeneintrag wiederholt
 *   <!--forq:repeat positionen--> … <!--/forq:repeat-->    dito, für handgeschriebene Vorlagen
 * In Wiederhol-Bereichen wird {{feld}} erst gegen den Eintrag, dann gegen die Wurzel aufgelöst.
 * Die Vorlagenstruktur ist Gestalter-erzeugt; nur die eingesetzten WERTE werden escaped (der Beleg
 * läuft im iframe). Ein fehlendes Feld wird zu leer, nicht zu einem sichtbaren Platzhalter.
 */
export function fillReportTemplate(html: string, data: Record<string, unknown>): string {
  const withCommentRepeats = html.replace(
    /<!--\s*forq:repeat\s+([\w.]+)\s*-->([\s\S]*?)<!--\s*\/forq:repeat\s*-->/g,
    (_match, path: string, inner: string) => {
      const list = resolveTemplateValue([data], path);
      if (!Array.isArray(list)) return '';
      return list.map((item) => fillTemplateScalars(inner, [item as Record<string, unknown>, data])).join('');
    },
  );
  const withRepeats = applyAttributeRepeats(withCommentRepeats, data);
  return fillTemplateScalars(withRepeats, [data]);
}

/** Druckrahmen (A4-Blatt + Druckknopf) um den befüllten Vorlagen-Körper, mit dessen eigenem CSS. */
function reportShell(lang: DocLang, title: string, accent: string, bodyHtml: string, css: string): string {
  const L = STRINGS[lang];
  return `<!doctype html>
<html lang="${esc(lang)}"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: ${esc(accent)}; color: #fff; }
  .sheet { width: 210mm; min-height: 297mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); display: flex; flex-direction: column; }
  [data-forq-zone="footer"] { margin-top: auto; }
${css}
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; min-height: 0; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">${esc(L.print)}</button></div>
  <div class="sheet">${bodyHtml}</div>
</body></html>`;
}

/** Blöcke, die im Grundbriefbogen-Modus der Rahmen trägt (nicht noch einmal im Inhalt). */
const baseFrameBlocks: ReadonlySet<OrderDocumentBlockId> = new Set(['parties', 'customerBox']);

/** Den belegtypischen Inhalt in den Grundbriefbogen einsetzen: alle {{beleg_inhalt}} ersetzen, sonst anhängen. */
function insertContentSlot(baseHtml: string, bodyHtml: string): string {
  return baseHtml.includes('{{beleg_inhalt}}') ? baseHtml.split('{{beleg_inhalt}}').join(bodyHtml) : `${baseHtml}\n${bodyHtml}`;
}

/**
 * Das vollständige HTML eines Vorgangs-Belegs -- zum Anzeigen im iframe (srcDoc) und Drucken.
 * Reihenfolge der Präzedenz:
 *   1. Grundbriefbogen (reportBase) gesetzt -> Rahmen + Inhalt (eigener Inhalt der Belegart oder der
 *      eingebaute Standard-Inhalt) in den {{beleg_inhalt}}-Slot.
 *   2. sonst: eigenständige Vollvorlage der Belegart (reportTemplates[kind]).
 *   3. sonst: eingebautes Block-Layout.
 * Ohne Vorlage/Rahmen ändert sich nichts.
 */
export function renderOrderDocumentHtml(ctx: OrderDocumentContext): string {
  const defaults = ctx.company.documentDefaults;
  const custom = defaults.reportTemplates?.[ctx.kind];
  const base = defaults.reportBase;
  const lang = langOf(ctx.order);
  const accent = ctx.company.letterhead.accentColor || '#e30613';
  const title = `${STRINGS[lang].title[ctx.kind]} ${ctx.documentNumber}`;

  if (base && base.html.trim()) {
    const data = buildOrderTemplateData(ctx);
    const bodyHtml = custom && custom.html.trim()
      ? custom.html
      : assembleOrderBody(ctx.kind, ctx.order, defaults, lang, baseFrameBlocks);
    const composed = insertContentSlot(base.html, bodyHtml);
    return reportShell(lang, title, accent, fillReportTemplate(composed, data), `${base.css || ''}\n${custom?.css || ''}`);
  }
  if (custom && custom.html.trim()) {
    return reportShell(lang, title, accent, fillReportTemplate(custom.html, buildOrderTemplateData(ctx)), custom.css || '');
  }
  return shell(ctx);
}

/**
 * Beispiel-Vorgang für die Live-Vorschau im Vorlagen-Editor -- ein realistischer, aber erfundener
 * Vorgang, damit man die eigenen Textbausteine im echten Belegrahmen sieht, ohne einen Vorgang anzulegen.
 */
export const previewSampleOrder: OrderRecord = {
  id: 'preview-sample', tenantId: 'preview', number: '202600042', profile: 'general',
  title: 'Gabelstapler-Wartung', status: 'in-progress', bucket: 'in-progress', priority: 'normal',
  customerId: 'preview-customer', customerNumber: '10001', customerName: 'Muster Handel GmbH',
  contactName: 'Frau Beispiel', contactEmail: 'einkauf@muster-handel.de', contactPhone: '02922 12345',
  machineId: 'preview-machine', machineNumber: 'M-0815', machineLabel: 'Linde H25 · Dieselstapler',
  assignedTo: 'M. Techniker', dueAt: '2026-08-14', notes: 'Vorschau-Beispiel',
  addresses: [
    { id: 'a1', kind: 'billing', label: 'Rechnungsadresse', companyName: 'Muster Handel GmbH', contactName: 'Frau Beispiel', street: 'Industriestraße 7', postalCode: '59457', city: 'Werl', countryCode: 'DE' },
    { id: 'a2', kind: 'delivery', label: 'Lieferadresse', companyName: 'Muster Handel GmbH · Lager', contactName: '', street: 'Am Hafen 3', postalCode: '59457', city: 'Werl', countryCode: 'DE' },
  ],
  conditions: {
    customerReference: 'BST-2026-4711', paymentTermDays: 14, cashDiscountPercent: 2, cashDiscountDays: 7,
    orderDiscountPercent: 0, requestedDate: '2026-08-12', deliveryMethod: 'Spedition', deliveryTerms: 'DAP – Geliefert benannter Ort (frei Haus)',
    documentDate: '2026-08-01', language: 'de', costCenterId: null, costCenterName: 'Werkstatt',
  },
  positions: [
    { id: 'p1', positionNumber: 1, kind: 'service', sourceId: 's1', sourceNumber: 'DL-100', machineId: null, description: 'Große Wartung inkl. UVV-Prüfung', quantity: 1, unit: 'Pauschal', unitPrice: 320, purchasePrice: 0, discountPercent: 0, netAmount: 320, vatRate: 19, vatAmount: 60.8, grossAmount: 380.8, internalText: 'Hydrauliköl + Filter tauschen', customerText: '' },
    { id: 'p2', positionNumber: 2, kind: 'article', sourceId: 'a10', sourceNumber: 'ET-4521', machineId: null, description: 'Hydraulikfilter', quantity: 2, unit: 'Stück', unitPrice: 34.5, purchasePrice: 0, discountPercent: 0, netAmount: 69, vatRate: 19, vatAmount: 13.11, grossAmount: 82.11, internalText: '', customerText: '' },
  ],
  subtotal: 389, positionDiscountTotal: 0, orderDiscountTotal: 0, netTotal: 389, vatTotal: 73.91, grossTotal: 462.91,
  statusHistory: [], createdAt: '2026-08-01T08:00:00.000Z', updatedAt: '2026-08-01T08:00:00.000Z',
};
